import { formatDate } from '@/lib/date-time';
/**
 * @file components/audit-tasks/field/SamplesPanel.tsx
 * @description 採樣送檢管理內聯面板（Table + 三模式 Drawer）。
 *
 * 從 routes/_app/audit/records/$caseId.sampling.tsx 搬遷邏輯，去 PageShell。
 * 三個模式共用一個 Drawer：mode = 'create' | 'edit' | 'view'。
 *
 * 「採樣送檢」拆兩步（序72）：
 *  - 【新增採樣】：選樣品分類（產品/水土壤/蜂蜜）→ 後端按 scheme+分類選採樣單模板
 *    （TOP：產品=D-ZHSR03-6、水土=D-ZHSR03-7；TAP：產品=TAP-ZHSR03-2、水土=D-ZHSR03-3、
 *    蜂蜜=TAP-ZHSR03-10）自動建採樣單 form_instance；含採樣位置手繪示意圖。
 *  - 【新增送檢】：對已採樣的 sample，POST /samples/:sid/submission → 後端按 scheme 選
 *    送檢單模板（TOP=D-ZHSR03-5、TAP=TAP-ZHSR03-1）建送檢單並寫 sentAt。
 *    未採樣（無記錄）時不可送檢；已送檢的記錄顯示「已送檢」並禁用送檢。
 *
 * Schema（M2 → Task 12 已更新）：
 * - samplingType enum: '現場採樣' | '年度後市場採樣' | '質譜快檢'
 * - 建立（POST）接受 samplingType / sampleDescription / collectedAt / labId / sentAt /
 *   sketchFileId（採樣位置手繪示意圖）
 * - 更新（PATCH）額外允許 result / reportReceivedAt / reportFileId / sketchFileId
 * - 送驗實驗室改為 FK（labId）+ 快照（labName）模式，下拉由 useLaboratoryOptions 提供
 *
 * 採樣位置示意圖（sketchFileId）：
 * - 列表 GET 投影不含此欄，由 GET /api/cases/:id/sample-sketches 補充映射後合併。
 * - 繪製走 <SamplingSketchPad>（canvas 手繪 → PNG 直傳 OSS），需在線；
 *   離線時禁用畫板入口（useNetworkStatus）。示意圖 fileId 作為普通欄位
 *   走既有 create/update 通道（僅使用者實際重繪/移除過才隨 PATCH 送出，
 *   避免離線讀不到映射時誤清空既有示意圖）。
 *
 * 新增採樣支援離線 native outbox；更新採樣走 patchWithOutbox。
 */

import { useMemo, useState } from 'react';
import { message } from '@/lib/antd-app';
import {
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClipboardPenLine, Edit3, Eye, PenLine, Plus, Send, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  SAMPLE_CATEGORY_LABELS,
  resolveSamplingTemplate,
  resolveSubmissionTemplate,
  type SampleCategory,
  type VerificationScheme,
} from '@zhcb/shared';
import { ApiError } from '@/lib/api-client';
import { apiClient } from '@/lib/api-client';
import { postWithOutbox, patchWithOutbox } from '@/lib/api-with-outbox';
import { useLaboratoryOptions } from '@/hooks/useLaboratories';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';
import { pushOutboxReliable } from '@/pwa/db';
import {
  SamplingSketchPad,
  SamplingSketchThumbnail,
} from '@/components/audit-tasks/field/SamplingSketchPad';

// ─── 類型與常數 ───────────────────────────────────────────────────────────────

const SAMPLING_TYPES = ['現場採樣', '年度後市場採樣', '質譜快檢'] as const;
type SamplingType = (typeof SAMPLING_TYPES)[number];

/**
 * 實驗室投影子集（來自 GET 採樣列表的 join 欄位）。
 * 用於展示「目前認證號」+ 停用徽章；舊資料的 lab 欄位可能為 null（防禦性）。
 */
interface SampleLab {
  id: number;
  name: string;
  accreditationNo: string | null;
  phone: string | null;
  isActive: boolean;
  deletedAt: string | null;
}

/** 採樣紀錄（GET 回傳形狀的最小子集，多餘欄位忽略） */
interface Sample {
  id: number;
  code: string;
  /** 驗證註冊碼（客戶註冊碼 ZH-CAS-xxxx，後端由案件所屬客戶投影） */
  clientCode: string | null;
  samplingType: SamplingType;
  /** 樣品分類（序72，舊資料可能為 null 視為 product） */
  sampleCategory: SampleCategory | null;
  sampleDescription: string | null;
  collectedAt: string | null;
  /** 樣品快照名稱（不隨實驗室改名變化） */
  labName: string;
  /** 當前實驗室 FK */
  labId: number;
  /** API GET 投影出當前 lab 詳情；用於展示「目前認證號」+ 停用徽章 */
  lab: SampleLab | null;
  sentAt: string | null;
  reportFileId: number | null;
  reportReceivedAt: string | null;
  result: string | null;
  /** 採樣單表單實例 ID（序72，採樣時自動建立） */
  samplingFormInstanceId: number | null;
  /** 送檢單表單實例 ID（序72，送檢時建立；非 null 表已送檢） */
  submissionFormInstanceId: number | null;
  createdAt: string;
}

/** Drawer 三種模式：新增、編輯、檢視（唯讀） */
type Mode = 'create' | 'edit' | 'view';

/** Drawer 表單值（dayjs 物件 + 字串） */
interface FormValues {
  samplingType: SamplingType;
  /** 樣品分類（序72，新增採樣時必選；決定採樣單模板） */
  sampleCategory?: SampleCategory;
  sampleDescription?: string | null;
  collectedAt?: Dayjs | null;
  /** 送驗實驗室 FK（送出時作為 labId payload） */
  labId: number;
  sentAt?: Dayjs | null;
  result?: string | null;
  reportReceivedAt?: Dayjs | null;
}

interface Props {
  /** 對應稽核案件 ID */
  caseId: number;
  /** 當前稽核任務 ID（正式表單頁路由與返回現場階段使用） */
  taskId: number;
  /** 案件驗證方案（決定送檢單/採樣單模板；null 時後端回退 TOP） */
  scheme: VerificationScheme | null;
}

/** 樣品分類下拉選項（序72：產品 / 水、土壤 / 蜂蜜產品） */
const SAMPLE_CATEGORY_OPTIONS = (Object.keys(SAMPLE_CATEGORY_LABELS) as SampleCategory[]).map(
  (v) => ({ value: v, label: SAMPLE_CATEGORY_LABELS[v] }),
);

// ─── 元件 ────────────────────────────────────────────────────────────────────

/**
 * 採樣管理內聯面板。
 *
 * 使用情境：被 PhaseField 嵌入到稽核任務頁，不再走獨立路由。
 */
export function SamplesPanel({ caseId, taskId, scheme }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // 序61：管理組（manager）唯讀——無採樣能力時隱藏「新增/編輯/送檢」，只保留「檢視」
  const { canSample } = useFieldWriteAccess();

  // 篩選條件（純前端 filter）
  const [filterType, setFilterType] = useState<SamplingType | undefined>(undefined);
  const [filterResult, setFilterResult] = useState<string | undefined>(undefined);

  // Drawer 狀態
  const [mode, setMode] = useState<Mode | null>(null);
  const [editing, setEditing] = useState<Sample | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm<FormValues>();

  // 監看樣品分類，動態顯示「將建立哪張採樣單/送檢單」提示（序72：分類→模板映射對前端可見）
  const watchedCategory = Form.useWatch('sampleCategory', form);
  const effectiveScheme: VerificationScheme = scheme ?? 'TOP';
  const samplingHint = watchedCategory
    ? resolveSamplingTemplate(effectiveScheme, watchedCategory)
    : null;
  const submissionTemplateCode = resolveSubmissionTemplate(effectiveScheme);

  // ─── 採樣位置示意圖狀態 ─────────────────────────────────────────────────────
  // 畫板產出 PNG 需在線直傳 OSS；離線時禁用畫板入口
  const { isOnline } = useNetworkStatus();
  /** 當前 Drawer 內的示意圖 fileId（create：待提交；edit/view：由映射查詢 seed） */
  const [sketchFileId, setSketchFileId] = useState<number | null>(null);
  /**
   * 使用者是否在本次 Drawer 內動過示意圖（重繪 / 移除）。
   * PATCH 僅在 touched 時才帶 sketchFileId：離線時映射查詢可能失敗（seed 為 null），
   * 若無條件送出會誤把既有示意圖清空。
   */
  const [sketchTouched, setSketchTouched] = useState(false);
  /** 手繪畫板 Modal 開關 */
  const [padOpen, setPadOpen] = useState(false);

  // ─── 列表查詢 ───────────────────────────────────────────────────────────────
  const samplesQ = useQuery<{ items: Sample[] }>({
    queryKey: ['samples', caseId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ items: Sample[] }>(`/api/cases/${caseId}/samples`, {
        scope: 'field-data',
      });
      return res.data;
    },
  });

  const filtered = (samplesQ.data?.items ?? []).filter((s) => {
    if (filterType && s.samplingType !== filterType) return false;
    if (filterResult && s.result !== filterResult) return false;
    return true;
  });

  // ─── 示意圖映射查詢 ─────────────────────────────────────────────────────────
  // 列表 GET 的投影為既有契約（不含 sketchFileId），由獨立輕量端點補充映射。
  const sketchesQ = useQuery<{ items: { sampleId: number; sketchFileId: number | null }[] }>({
    queryKey: ['sample-sketches', caseId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{
        items: { sampleId: number; sketchFileId: number | null }[];
      }>(`/api/cases/${caseId}/sample-sketches`, { scope: 'field-data' });
      return res.data;
    },
  });

  /** sampleId → sketchFileId 映射（開 Drawer 時 seed 當前示意圖） */
  const sketchMap = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const it of sketchesQ.data?.items ?? []) map.set(it.sampleId, it.sketchFileId);
    return map;
  }, [sketchesQ.data]);

  /** 從採樣清單進入正式採樣單／送檢單，返回時固定回到現場「採樣送檢」所在階段。 */
  const openFormInstance = (instanceId: number) => {
    void navigate({
      to: '/audit-tasks/$taskId/form/$instanceId',
      params: { taskId: String(taskId), instanceId: String(instanceId) },
      // 序21：返回時定位到「採樣送檢」頁籤
      search: { from: 'field', fromTab: 'sampling' },
    });
  };

  // ─── 新增採樣 ───────────────────────────────────────────────────────────────
  const createSample = async (values: FormValues) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // POST 只接受非 null 欄位；空字串轉 undefined 以省略 key
      const body = {
        samplingType: values.samplingType,
        // 樣品分類（序72）：後端據此 + scheme 選採樣單模板並自動建 form_instance
        sampleCategory: values.sampleCategory,
        sampleDescription: values.sampleDescription || undefined,
        collectedAt: values.collectedAt?.toISOString(),
        labId: values.labId,
        // 送檢時間不在「新增採樣」步驟填（改由「新增送檢」寫入），故不送
        // 採樣位置示意圖（畫板已上傳 OSS 的 fileId；未繪製時省略）
        sketchFileId: sketchFileId ?? undefined,
      };
      const url = `/api/cases/${caseId}/samples`;
      const outboxMeta = {
        entityType: 'sample',
        caseId,
      };
      const result: { offline: true } | { item: Sample } = !navigator.onLine
        ? await pushOutboxReliable({ method: 'POST', url, body, ...outboxMeta }).then(() => ({
            offline: true as const,
          }))
        : await postWithOutbox<{ item: Sample }>(url, body, outboxMeta);

      // 離線：result = { offline: true }；提示已入佇列。
      if ('offline' in result) {
        message.success('採樣已加入離線佇列，連線後自動同步');
      } else if (result.item.samplingFormInstanceId == null) {
        message.warning('採樣已登記，但正式採樣單模板尚未啟用，請通知管理員至「表單管理」更新表單');
      } else {
        message.success('採樣已登記，請繼續完成正式採樣單');
      }
      closeDrawer();
      void qc.invalidateQueries({ queryKey: ['samples', caseId] });
      void qc.invalidateQueries({ queryKey: ['sample-sketches', caseId] });
      if (!('offline' in result) && result.item.samplingFormInstanceId != null) {
        openFormInstance(result.item.samplingFormInstanceId);
      }
    } catch (e) {
      // 後端 INVALID_LAB（400）：通常意味本地下拉快取過期。主動失效讓下次 open 拿到最新列表
      if (e instanceof ApiError && e.status === 400) {
        void qc.invalidateQueries({ queryKey: ['settings', 'laboratories', 'options'] });
      }
      message.error(e instanceof Error ? e.message : '採樣提交失敗');
    } finally {
      setIsCreating(false);
    }
  };

  // ─── Mutation：編輯採樣 ─────────────────────────────────────────────────────
  // 改走 patchWithOutbox：線上直發 API，離線時推 PWA outbox 排隊。
  const update = useMutation({
    mutationFn: ({ id, values }: { id: number; values: FormValues }) => {
      // PATCH 允許 null 清除欄位；undefined 表示不變
      const body = {
        samplingType: values.samplingType,
        sampleDescription: values.sampleDescription ?? null,
        collectedAt: values.collectedAt ? values.collectedAt.toISOString() : null,
        labId: values.labId,
        result: values.result ?? null,
        reportReceivedAt: values.reportReceivedAt ? values.reportReceivedAt.toISOString() : null,
        // 示意圖：僅在使用者本次動過（重繪/移除）時才送出，避免離線 seed 失敗誤清空
        ...(sketchTouched ? { sketchFileId } : {}),
      };
      return patchWithOutbox(`/api/cases/${caseId}/samples/${id}`, body, {
        entityType: 'sample',
        entityClientLocalId: String(id),
      });
    },
    onSuccess: (result) => {
      if ((result as { offline?: boolean })?.offline) {
        message.success('採樣更新已加入離線佇列，連線後自動同步');
      } else {
        message.success('採樣已更新');
      }
      closeDrawer();
      void qc.invalidateQueries({ queryKey: ['samples', caseId] });
      void qc.invalidateQueries({ queryKey: ['sample-sketches', caseId] });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.status === 400) {
        void qc.invalidateQueries({ queryKey: ['settings', 'laboratories', 'options'] });
      }
      message.error(e.message);
    },
  });

  // ─── 新增送檢（序72 拆兩步第二步）──────────────────────────────────────────────
  // 對已採樣的 sample 呼叫送檢端點：後端按 scheme 選送檢單模板建 form_instance + 寫 sentAt。
  // 送檢屬在線動作（需選送檢單模板 + 寫關聯），不走離線 outbox。
  const submitSubmission = useMutation({
    mutationFn: (sampleId: number) =>
      apiClient.post<{ item: Sample }>(`/api/cases/${caseId}/samples/${sampleId}/submission`, {
        // 送檢時間預設為當下（後端未帶時亦以當下時間寫入）
        sentAt: new Date().toISOString(),
      }),
    onSuccess: (data) => {
      if (data.item.submissionFormInstanceId == null) {
        message.warning('送檢已登記，但送檢單模板尚未啟用，請通知管理員至「表單管理」更新表單');
      } else {
        message.success('送檢已登記，請繼續完成送檢單');
      }
      void qc.invalidateQueries({ queryKey: ['samples', caseId] });
      if (data.item.submissionFormInstanceId != null) {
        openFormInstance(data.item.submissionFormInstanceId);
      }
    },
    onError: (e: Error) => {
      message.error(e.message);
    },
  });

  // ─── Drawer 開關 ────────────────────────────────────────────────────────────
  const closeDrawer = () => {
    setMode(null);
    setEditing(null);
    setSketchFileId(null);
    setSketchTouched(false);
    setPadOpen(false);
    form.resetFields();
  };

  /** 依採樣記錄 seed 示意圖狀態（edit / view 共用） */
  const seedSketch = (s: Sample) => {
    setSketchFileId(sketchMap.get(s.id) ?? null);
    setSketchTouched(false);
  };

  const openCreate = () => {
    setMode('create');
    setEditing(null);
    setSketchFileId(null);
    setSketchTouched(false);
    form.resetFields();
    // 新增時使用常用現場預設值：現場採樣 + 產品分類（序72 樣品分類預設 product）。
    form.setFieldsValue({ samplingType: '現場採樣', sampleCategory: 'product' });
  };

  const openEdit = (s: Sample) => {
    setMode('edit');
    setEditing(s);
    seedSketch(s);
    form.setFieldsValue({
      samplingType: s.samplingType,
      sampleCategory: s.sampleCategory ?? 'product',
      sampleDescription: s.sampleDescription,
      collectedAt: s.collectedAt ? dayjs(s.collectedAt) : null,
      labId: s.labId,
      result: s.result,
      reportReceivedAt: s.reportReceivedAt ? dayjs(s.reportReceivedAt) : null,
    });
  };

  const openView = (s: Sample) => {
    setMode('view');
    setEditing(s);
    seedSketch(s);
    form.setFieldsValue({
      samplingType: s.samplingType,
      sampleCategory: s.sampleCategory ?? 'product',
      sampleDescription: s.sampleDescription,
      collectedAt: s.collectedAt ? dayjs(s.collectedAt) : null,
      labId: s.labId,
      sentAt: s.sentAt ? dayjs(s.sentAt) : null,
      result: s.result,
      reportReceivedAt: s.reportReceivedAt ? dayjs(s.reportReceivedAt) : null,
    });
  };

  /** Form 提交分流到 create / update */
  const submit = (values: FormValues) => {
    if (mode === 'create') {
      void createSample(values);
    } else if (mode === 'edit' && editing) {
      update.mutate({ id: editing.id, values });
    }
  };

  // ─── 實驗室下拉選項 ──────────────────────────────────────────────────────────
  // 一次拉取 500 條啟用中的實驗室，前端 Select 內過濾，5 分鐘快取。
  const labOptionsQ = useLaboratoryOptions();
  const labOptions = labOptionsQ.data?.items ?? [];

  // ─── 表格欄位 ───────────────────────────────────────────────────────────────
  const cols: ColumnsType<Sample> = [
    {
      // 序：採樣送檢清單「編號」改用驗證註冊碼（回落樣品碼）
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 200,
      render: (v: string | null, r) => (
        <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{v || r.code}</span>
      ),
    },
    { title: '類型', dataIndex: 'samplingType', width: 120 },
    {
      title: '樣品分類',
      dataIndex: 'sampleCategory',
      width: 110,
      // 舊資料無分類時視為「產品」
      render: (v: SampleCategory | null) => SAMPLE_CATEGORY_LABELS[v ?? 'product'],
    },
    {
      title: '描述',
      dataIndex: 'sampleDescription',
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '採樣時間',
      dataIndex: 'collectedAt',
      width: 140,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '實驗室',
      dataIndex: 'labName',
      width: 180,
      render: (v: string, row) => (
        <Space size={4}>
          <span>{v}</span>
          {row.lab && (!row.lab.isActive || row.lab.deletedAt) && <Tag color="default">已停用</Tag>}
        </Space>
      ),
    },
    // 序：採樣送檢不必有結果，清單移除【結果】列（結果改由實驗室檢驗報告判定）
    {
      title: '送檢',
      width: 110,
      render: (_, r) =>
        r.submissionFormInstanceId != null || r.sentAt ? (
          <Tag color="success">已送檢</Tag>
        ) : (
          <Tag color="default">未送檢</Tag>
        ),
    },
    {
      title: '操作',
      width: canSample ? 360 : 210,
      render: (_, r) => (
        <Space size="small">
          <Button type="link" size="small" icon={<Eye size={12} />} onClick={() => openView(r)}>
            檢視
          </Button>
          {r.samplingFormInstanceId != null && (
            <Button
              type="link"
              size="small"
              icon={<ClipboardPenLine size={12} />}
              onClick={() => openFormInstance(r.samplingFormInstanceId!)}
            >
              {canSample ? '填寫採樣單' : '檢視採樣單'}
            </Button>
          )}
          {!canSample && r.submissionFormInstanceId != null && (
            <Button
              type="link"
              size="small"
              icon={<ClipboardPenLine size={12} />}
              onClick={() => openFormInstance(r.submissionFormInstanceId!)}
            >
              檢視送檢單
            </Button>
          )}
          {/* 序61：無採樣能力（如管理組）僅可檢視，不顯示編輯/送檢 */}
          {canSample && (
            <>
              <Button
                type="link"
                size="small"
                icon={<Edit3 size={12} />}
                onClick={() => openEdit(r)}
              >
                編輯
              </Button>
              {/* 新增送檢（序72 第二步）：已送檢的記錄禁用；送檢屬在線動作 */}
              {r.submissionFormInstanceId != null ? (
                <Button
                  type="link"
                  size="small"
                  icon={<ClipboardPenLine size={12} />}
                  onClick={() => openFormInstance(r.submissionFormInstanceId!)}
                >
                  填寫送檢單
                </Button>
              ) : (
                <Button
                  type="link"
                  size="small"
                  icon={<Send size={12} />}
                  disabled={!isOnline}
                  loading={submitSubmission.isPending && submitSubmission.variables === r.id}
                  onClick={() => submitSubmission.mutate(r.id)}
                >
                  {r.sentAt ? '補建送檢單' : '新增送檢'}
                </Button>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  // ─── Drawer 標題與唯讀判斷 ───────────────────────────────────────────────────
  const drawerTitle =
    mode === 'create'
      ? '新增採樣'
      : mode === 'edit'
        ? `編輯採樣 ${editing?.code ?? ''}`
        : `採樣詳情 ${editing?.code ?? ''}`;
  const isReadonly = mode === 'view';

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* 篩選列 + 新增按鈕 */}
        <Space wrap>
          <Select
            allowClear
            placeholder="採樣類型"
            style={{ width: 160 }}
            value={filterType}
            onChange={setFilterType}
            options={SAMPLING_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <Select
            allowClear
            placeholder="結果狀態"
            style={{ width: 140 }}
            value={filterResult}
            onChange={setFilterResult}
            options={[
              { value: 'pass', label: 'PASS' },
              { value: 'fail', label: 'FAIL' },
              { value: 'pending', label: 'PENDING' },
            ]}
          />
          {/* 序61：無採樣能力（如管理組）不顯示「新增採樣」 */}
          {canSample && (
            <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
              新增採樣
            </Button>
          )}
        </Space>

        {/* 列表 */}
        {samplesQ.isLoading ? (
          <Typography.Text>載入中…</Typography.Text>
        ) : filtered.length ? (
          <Table<Sample>
            rowKey="id"
            dataSource={filtered}
            columns={cols}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            scroll={{ x: 1280 }}
          />
        ) : (
          <Empty description="尚無採樣紀錄" />
        )}
      </Space>

      {/* 三模式共用 Drawer */}
      <Drawer
        title={drawerTitle}
        open={mode != null}
        onClose={closeDrawer}
        width={520}
        footer={
          isReadonly ? (
            <Button onClick={closeDrawer}>關閉</Button>
          ) : (
            <Space>
              <Button onClick={closeDrawer}>取消</Button>
              <Button
                type="primary"
                loading={isCreating || update.isPending}
                onClick={() => form.submit()}
              >
                {mode === 'create' ? '提交' : '儲存'}
              </Button>
            </Space>
          )
        }
      >
        <Form form={form} layout="vertical" onFinish={submit} disabled={isReadonly}>
          <Form.Item name="samplingType" label="採樣類型" rules={[{ required: true }]}>
            <Select options={SAMPLING_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          {/* 樣品分類（序72）：決定採樣單模板；僅新增時可選（編輯不改分類避免採樣單模板漂移） */}
          <Form.Item
            name="sampleCategory"
            label="樣品分類"
            rules={[{ required: mode === 'create', message: '請選擇樣品分類' }]}
            tooltip="決定採樣單表單（產品/水土壤/蜂蜜按驗證方案自動選單）"
          >
            <Select options={SAMPLE_CATEGORY_OPTIONS} disabled={mode !== 'create'} />
          </Form.Item>
          {/* 採樣單/送檢單模板提示（序72：分類 + 方案 → 具體採樣單；送檢單依方案）*/}
          {mode === 'create' && samplingHint && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: -12, marginBottom: 12 }}
            >
              將建立採樣單：{samplingHint.templateCode}
              {samplingHint.fallback && '（該方案無此分類專屬採樣單，已回退產品採樣單）'}
              ；送檢單：{submissionTemplateCode}
            </Typography.Text>
          )}
          <Form.Item name="sampleDescription" label="樣品描述">
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
          {/* 採樣位置示意圖：對應紙本採樣單的田區採樣位置手繪圖（canvas 畫板輸出 PNG） */}
          <Form.Item label="採樣位置示意圖">
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {sketchFileId != null ? (
                <>
                  {/* 有圖：縮略圖（點擊放大）+（編輯態）重繪 / 移除 */}
                  <SamplingSketchThumbnail fileId={sketchFileId} />
                  {!isReadonly && (
                    <Space size="small">
                      <Button
                        size="small"
                        icon={<PenLine size={12} />}
                        disabled={!isOnline}
                        onClick={() => setPadOpen(true)}
                      >
                        重繪
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<Trash2 size={12} />}
                        onClick={() => {
                          setSketchFileId(null);
                          setSketchTouched(true);
                        }}
                      >
                        移除
                      </Button>
                    </Space>
                  )}
                </>
              ) : isReadonly ? (
                <Typography.Text type="secondary">—</Typography.Text>
              ) : (
                <Button
                  size="small"
                  icon={<PenLine size={12} />}
                  disabled={!isOnline}
                  onClick={() => setPadOpen(true)}
                >
                  繪製示意圖
                </Button>
              )}
              {/* 離線提示：畫板輸出 PNG 需在線直傳 OSS */}
              {!isReadonly && !isOnline && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  目前離線，示意圖繪製需連線後使用（其餘欄位仍可離線保存）
                </Typography.Text>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="collectedAt" label="採樣時間">
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="labId"
            label="送驗實驗室"
            rules={[{ required: true, message: '請選擇實驗室' }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="選擇實驗室"
              loading={labOptionsQ.isLoading}
              optionFilterProp="searchText"
              filterOption={(input, opt) =>
                ((opt?.searchText as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={[
                // 已選的 labId 但已停用 / 軟刪 → 加入 disabled option，避免「值找不到」顯示問題
                ...(editing?.lab && (!editing.lab.isActive || editing.lab.deletedAt)
                  ? [
                      {
                        value: editing.lab.id,
                        searchText: editing.lab.name,
                        label: (
                          <span>
                            {editing.lab.name} <Tag color="default">已停用</Tag>
                          </span>
                        ),
                        disabled: true,
                      },
                    ]
                  : []),
                ...labOptions.map((lab) => ({
                  value: lab.id,
                  searchText: `${lab.name} ${lab.accreditationNo ?? ''}`,
                  label: (
                    <Space size={4}>
                      <span>{lab.name}</span>
                      {(lab.accreditationNo || lab.contactPerson) && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {[lab.accreditationNo, lab.contactPerson].filter(Boolean).join(' · ')}
                        </Typography.Text>
                      )}
                    </Space>
                  ),
                })),
              ]}
            />
          </Form.Item>
          {mode !== 'create' && (
            <>
              <Form.Item name="result" label="檢測結果">
                <Select
                  allowClear
                  options={[
                    { value: 'pass', label: 'PASS（合格）' },
                    { value: 'fail', label: 'FAIL（不合格）' },
                    { value: 'pending', label: 'PENDING（待覆驗）' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="reportReceivedAt" label="報告收到時間">
                <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
          {mode === 'view' && (
            <Form.Item name="sentAt" label="送驗時間">
              <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} disabled />
            </Form.Item>
          )}
          {/* 檢視模式：顯示實驗室快照 + 目前認證號（不走 Select，直接文字展示） */}
          {mode === 'view' && editing && (
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                送驗實驗室快照：{editing.labName}
                {editing.lab && (!editing.lab.isActive || editing.lab.deletedAt) && (
                  <Tag color="default" style={{ marginLeft: 8 }}>
                    已停用
                  </Tag>
                )}
              </Typography.Text>
              {editing.lab?.accreditationNo && (
                <div>
                  <Typography.Text type="secondary">
                    目前認證號：{editing.lab.accreditationNo}
                  </Typography.Text>
                </div>
              )}
            </div>
          )}
        </Form>
      </Drawer>

      {/* 手繪畫板 Modal：完成後 PNG 直傳 OSS，回填 fileId（提交時隨 create/update 落庫） */}
      <SamplingSketchPad
        open={padOpen}
        caseId={caseId}
        onClose={() => setPadOpen(false)}
        onUploaded={(file) => {
          setSketchFileId(file.id);
          setSketchTouched(true);
        }}
      />
    </>
  );
}
